// SPDX-FileCopyrightText: Copyright 2025 OP Financial Group (https://op.fi). All Rights Reserved.
//
// SPDX-License-Identifier: LicenseRef-OpAllRightsReserved

import React, { useState, useContext } from 'react';
import Preview from './Preview';
import Confirmation from './Confirmation';
import NavigationButtons from '../../components/NavigationButtons';
import { getPreviewPageErrors, validatePreviewPage } from '../../validation/Validation';
import { saveDataToSession, saveAdminFlowDataToSession } from '../../Session';
import MainHeader from '../../components/MainHeader';
import { id } from '../../Constants';
import InputErrorTile, { ErrorItem } from '../../components/InputErrorTile';
import { ContextWrapper, ContractData } from '../../context/ContextWrapper';

interface PreviewAndConfirmationPageProps {
    backPath: string;
    continuePath: string;
}

const PreviewAndConfirmationPage: React.FC<PreviewAndConfirmationPageProps> = ({ backPath, continuePath }) => {
    const [errors, setErrors] = useState<ErrorItem[]>([]);
    const { isAdminFlow, setIsDataSavingInProgress } = useContext(ContextWrapper);

    const continueProcess = (contractData: ContractData, updateInformation: any): boolean => {
        const isSuccessful = isAdminFlow
            ? validatePreviewPage(contractData, updateInformation) &&
              saveAdminFlowDataToSession(contractData, setIsDataSavingInProgress)
            : validatePreviewPage(contractData, updateInformation) &&
              saveDataToSession(contractData, setIsDataSavingInProgress);
        if (isSuccessful) {
            window.scrollTo(0, 0);
            (document.activeElement as HTMLElement)?.blur();
        } else {
            setErrors(getPreviewPageErrors(contractData));
            const alert = document.getElementById(id.ALERT_NOTIFICATION);
            alert?.scrollIntoView({ behavior: 'smooth' });
            (document.activeElement as HTMLElement)?.blur();
        }
        return isSuccessful as boolean;
    };

    return (
        <>
            <MainHeader />
            <Preview />
            <div className="alert-notification" id={id.ALERT_NOTIFICATION} aria-hidden="true" />
            <InputErrorTile errors={errors} />
            <Confirmation />
            <NavigationButtons
                continueProcess={continueProcess}
                backPath={backPath}
                continuePath={continuePath}
                cancelEnabled
            />
            <div className="opux-spacing-bottom-margin-double" />
        </>
    );
};

export default PreviewAndConfirmationPage;
